import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuthContext } from '@/lib/AuthProvider';

interface ShippingAddress {
  complete: boolean;
  value: {
    name: string;
    phone: string;
    email?: string;
    address: {
      line1: string;
      line2?: string;
      city: string;
      state: string;
      postal_code: string;
      country: string;
    };
  };
}

interface ShippingAddressContextType {
  shippingAddress: ShippingAddress | null;
  setShippingAddress: (address: ShippingAddress | null) => void;
  isAddressComplete: boolean;
  clearShippingAddress: () => void;
}

const ShippingAddressContext = createContext<ShippingAddressContextType | undefined>(undefined);

export const useShippingAddress = () => {
  const context = useContext(ShippingAddressContext);
  if (context === undefined) {
    throw new Error('useShippingAddress must be used within a ShippingAddressProvider');
  }
  return context;
};

interface ShippingAddressProviderProps {
  children: React.ReactNode;
}

export const ShippingAddressProvider: React.FC<ShippingAddressProviderProps> = ({ children }) => {
  const [shippingAddress, setShippingAddressState] = useState<ShippingAddress | null>(null);
  const [lastUserId, setLastUserId] = useState<string | null>(null);
  const { user } = useAuthContext();

  // Load shipping address from sessionStorage on mount
  useEffect(() => {
    const savedAddress = sessionStorage.getItem('shipping-address');
    const savedUserId = sessionStorage.getItem('shipping-address-user-id');
    
    if (savedAddress && savedUserId) {
      try {
        const parsedAddress = JSON.parse(savedAddress);
        setShippingAddressState(parsedAddress);
        setLastUserId(savedUserId);
      } catch (error) {
        sessionStorage.removeItem('shipping-address');
        sessionStorage.removeItem('shipping-address-user-id');
      }
    }
  }, []);

  // Clear shipping address when user changes (logout/login with different account)
  useEffect(() => {
    const currentUserId = user?.uid || null;
    
    // Clear shipping address if user logs out or switches to a different account
    if (!user || (lastUserId && currentUserId !== lastUserId)) {
      setShippingAddressState(null);
      sessionStorage.removeItem('shipping-address');
      sessionStorage.removeItem('shipping-address-user-id');
    }
    
    // Update the last user ID
    setLastUserId(currentUserId);
  }, [user, lastUserId]);

  const setShippingAddress = (address: ShippingAddress | null) => {
    setShippingAddressState(address);
    
    // Save to sessionStorage
    if (address && user) {
      sessionStorage.setItem('shipping-address', JSON.stringify(address));
      sessionStorage.setItem('shipping-address-user-id', user.uid);
    } else {
      sessionStorage.removeItem('shipping-address');
      sessionStorage.removeItem('shipping-address-user-id');
    }
  };

  const clearShippingAddress = () => {
    setShippingAddress(null);
  };

  const isAddressComplete = shippingAddress?.complete === true;

  const value: ShippingAddressContextType = {
    shippingAddress,
    setShippingAddress,
    isAddressComplete,
    clearShippingAddress,
  };

  return (
    <ShippingAddressContext.Provider value={value}>
      {children}
    </ShippingAddressContext.Provider>
  );
};
